import api from '@/lib/axios'

// ============================================================================
//  hr.api.ts — Couche API Module RH
//  Consomme /api/v1/hr (HrController — Phase 3)
// ============================================================================

// ─── Types Employés ───────────────────────────────────────────────────────────

export interface Employee {
  id: string
  matricule: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  jobTitle: string
  status: 'ACTIVE' | 'ON_LEAVE' | 'SUSPENDED' | 'TERMINATED'
  hireDate: string
  avatarUrl?: string
  departmentId?: string
  managerId?: string
  department?: { id: string; name: string }
  manager?: { id: string; firstName: string; lastName: string }
  ipresId?: string
  cssId?: string
  ninea?: string
  // [Payroll] Situation familiale
  maritalStatus?:     'CELIBATAIRE' | 'MARIE' | 'DIVORCE' | 'VEUF' | 'UNION_LIBRE'
  spouseEmployed?:    boolean
  dependentChildren?: number
  otherDependents?:   number
  fiscalParts?:       number
  // [Pointage] Règle d'horaires assignée (null = règle par défaut de l'entité)
  attendanceRuleId?:  string | null
  createdAt: string
}

export interface EmployeeStats {
  totalActive: number
  newThisMonth: number
  onLeave: number
  expiringContracts: number
}

// ─── Types Contrats ───────────────────────────────────────────────────────────

export interface Contract {
  id: string
  employeeId: string
  type: 'CDI' | 'CDD' | 'STAGE' | 'INTERIM'
  status: 'DRAFT' | 'CONFIRMED' | 'TRIAL_PERIOD' | 'ACTIVE' | 'EXPIRED' | 'TERMINATED'
  startDate: string
  endDate?: string
  grossSalary: number
  currency: string
  position: string
  trialPeriodMonths?: number
  renewalCount: number
  // [Payroll] Champs paie Sénégal
  isCadre?:        boolean
  category?:       string
  daysPerMonth?:   number
  paymentMethod?:  'BANK_TRANSFER' | 'CHECK' | 'CASH'
  bankAccountIban?: string
  employee?: Pick<Employee, 'firstName' | 'lastName' | 'matricule'>
  salaryComponents?: ContractSalaryComponent[]
}

// ─── Types Paie ───────────────────────────────────────────────────────────────

export interface PayrollPeriod {
  id: string
  year: number
  month: number
  status: 'OPEN' | 'CALCULATED' | 'VALIDATED' | 'APPROVED' | 'PAID' | 'LOCKED'
  openedAt: string
  closedAt?: string
  _count?: { payslips: number }
  stats?: {
    totalGross: number
    totalNet: number
    totalEmployer: number
    coutTotal: number
    effectif: number
  }
}

export interface Payslip {
  id: string
  employeeId: string
  periodId: string
  grossSalary: number
  netSalary: number
  netPayable: number
  totalDeductions: number
  employerCharges: number
  status: string
  currency: string
  pdfUrl?: string
  employee?: Pick<Employee, 'firstName' | 'lastName' | 'matricule' | 'jobTitle'>
  period?: Pick<PayrollPeriod, 'year' | 'month' | 'status'>
  lines?: PayslipLine[]
}

export interface PayslipLine {
  id: string
  type: string
  code: string
  label: string
  amount: number
  isDeduction: boolean
  base?: number
  rate?: number
  order: number
}

// ─── Types Congés ─────────────────────────────────────────────────────────────

export interface LeaveType {
  id: string
  code: string
  name: string
  isPaid: boolean
  requiresDoc: boolean
  maxDaysPerYear?: number
  noticeDaysRequired: number
  color?: string
  isActive: boolean
}

export interface LeaveBalance {
  id: string
  leaveTypeId: string
  year: number
  entitlement: number
  taken: number
  pending: number
  carriedOver: number
  remaining: number
  leaveType: Pick<LeaveType, 'code' | 'name' | 'color' | 'isPaid'>
}

export interface LeaveRequest {
  id: string
  employeeId: string
  leaveTypeId: string
  startDate: string
  endDate: string
  totalDays: number
  status: 'PENDING' | 'MANAGER_APPROVED' | 'HR_APPROVED' | 'APPROVED' | 'REJECTED' | 'CANCELLED'
  reason?: string
  documentUrl?: string
  employee?: Pick<Employee, 'id' | 'firstName' | 'lastName' | 'matricule'>
  leaveType?: Pick<LeaveType, 'code' | 'name' | 'color' | 'isPaid'>
  approvalHistory?: Array<{
    action: string
    actedById: string
    comment?: string
    createdAt: string
  }>
}

// ─── Types Pointage ───────────────────────────────────────────────────────────

export interface Attendance {
  id: string
  employeeId: string
  date: string
  clockIn?: string
  clockOut?: string
  breakMinutes: number
  totalMinutes?: number
  overtimeMinutes?: number
  lateMinutes?: number
  status: 'PRESENT' | 'LATE' | 'HALF_DAY' | 'ABSENT' | 'ON_LEAVE'
  /** Aligné sur l'enum Prisma AttendanceSource. */
  source: 'WEB' | 'MOBILE' | 'BIOMETRIC' | 'MANUAL'
  /** N° de série du terminal ayant produit le pointage (source BIOMETRIC). */
  deviceId?: string | null
  notes?: string
}

export interface AttendanceRule {
  id: string
  entityId: string
  name: string
  workStartTime: string
  workEndTime: string
  lunchBreakMinutes: number
  weeklyHours: number
  overtimeThreshold: number
  lateThreshold: number
  debounceMinutes: number
  isDefault: boolean
}

export interface DailyOverviewCounts {
  total: number
  present: number
  onLeave: number
  absent: number
  late: number
}

export interface DailyOverview {
  date: string
  summary: DailyOverviewCounts
  employees: Array<{
    id: string
    firstName: string
    lastName: string
    matricule: string
    /** Terminal de rattachement de l'employé (null = non rattaché). */
    zkDeviceSn?: string | null
    attendance?: Attendance
    leave?: LeaveRequest
    status: string
  }>
  /** Compteurs par terminal ; le groupe deviceSn = null (non rattachés) arrive en dernier. */
  byDevice?: Array<DailyOverviewCounts & { deviceSn: string | null }>
}

// ─── Types Carrière ───────────────────────────────────────────────────────────

export interface EvaluationCampaign {
  id: string
  name: string
  type: 'ANNUAL' | 'SEMI_ANNUAL' | 'TRIAL_PERIOD' | 'QUARTERLY' | 'PROJECT_BASED'
  status: 'DRAFT' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED'
  startDate: string
  endDate: string
  dueDate: string
  description?: string
  _count?: { evaluations: number }
}

export interface PerformanceEvaluation {
  id: string
  employeeId: string
  campaignId: string
  status: string
  selfEvalScore?: number
  managerScore?: number
  finalScore?: number
  strengthPoints?: string
  improvementPoints?: string
  employee?: Pick<Employee, 'firstName' | 'lastName' | 'matricule' | 'jobTitle'>
  campaign?: Pick<EvaluationCampaign, 'name' | 'type' | 'dueDate' | 'status'>
}

export interface TrainingCatalog {
  id: string
  name: string
  type: string
  description?: string
  provider?: string
  duration?: number
  cost?: number
  isActive: boolean
}

export interface CareerHistory {
  id: string
  employeeId: string
  type: string
  description: string
  effectiveDate: string
  fromJobTitle?: string
  toJobTitle?: string
  fromSalary?: number
  toSalary?: number
  notes?: string
}

// ─── Pagination helper ────────────────────────────────────────────────────────

interface Paginated<T> {
  data: T[]
  total: number
  page: number
  limit: number
  pages: number
}

// ─── Types Module Paie ────────────────────────────────────────────────────────

export type PayrollVariableType =
  | 'BONUS' | 'BONUS_NON_TAXABLE'
  | 'OVERTIME_125' | 'OVERTIME_150' | 'OVERTIME_200'
  | 'COMMISSION' | 'TRANSPORT_EXCESS' | 'ALLOWANCE_OTHER'
  | 'DAYS_WORKED' | 'DAYS_ABSENT_UNPAID' | 'DAYS_SICK_PAID' | 'DAYS_LEAVE_PAID'
  | 'ADVANCE_REPAYMENT' | 'LOAN_REPAYMENT' | 'GARNISHMENT'
  | 'UNION_DUES' | 'HEALTH_INSURANCE' | 'DEDUCTION_OTHER'

export interface PayrollVariable {
  id:                 string
  payslipId:          string
  type:               PayrollVariableType
  code:               string
  label:              string
  quantity:           number | null
  unitValue:          number | null
  amount:             number
  isImposable:        boolean
  isSubjectToIpres:   boolean
  isSubjectToCss:     boolean
  isSubjectToCfce:    boolean
  isDeduction:        boolean
  description:        string | null
  attachmentUrl:      string | null
  createdAt:          string
}

export interface CreatePayrollVariableDto {
  type:               PayrollVariableType
  code:               string
  label:              string
  amount:             number
  quantity?:          number
  unitValue?:         number
  isImposable?:       boolean
  isSubjectToIpres?:  boolean
  isSubjectToCss?:    boolean
  isSubjectToCfce?:   boolean
  isDeduction?:       boolean
  description?:       string
  attachmentUrl?:     string
}

// ─── Types Composants Salaire (Contrat) ──────────────────────────────────────

export type SalaryComponentType =
  | 'BASE_SALARY' | 'SURSALARY'
  | 'TRANSPORT_ALLOWANCE' | 'HOUSING_ALLOWANCE'
  | 'SENIORITY_BONUS' | 'PERFORMANCE_BONUS' | 'PRESENCE_BONUS'
  | 'FUNCTION_ALLOWANCE' | 'PHONE_ALLOWANCE' | 'INTERNET_ALLOWANCE'
  | 'IN_KIND_BENEFIT' | 'OTHER_FIXED_INCOME'

export interface ContractSalaryComponent {
  id:                 string
  contractId:         string
  type:               SalaryComponentType
  code:               string
  label:              string
  amount:             number
  isImposable:        boolean
  isSubjectToIpres:   boolean
  isSubjectToCss:     boolean
  isSubjectToCfce:    boolean
  subjectToProrata:   boolean
  order:              number
  notes:              string | null
  effectiveDate:      string
  endDate:            string | null
  createdAt:          string
}

export interface CreateContractComponentDto {
  type:               SalaryComponentType
  code:               string
  label:              string
  amount:             number
  isImposable?:       boolean
  isSubjectToIpres?:  boolean
  isSubjectToCss?:    boolean
  isSubjectToCfce?:   boolean
  subjectToProrata?:  boolean
  order?:             number
  notes?:             string
}

export interface PreviewPayrollDto {
  fiscalParts:    number
  isCadre:        boolean
  daysPerMonth:   number
  daysWorked?:    number
  components: Array<{
    type:                string
    code:                string
    label:               string
    amount:              number
    isImposable:         boolean
    isSubjectToIpres:    boolean
    isSubjectToCss:      boolean
    isSubjectToCfce:     boolean
    subjectToProrata:    boolean
  }>
  variables?: Array<{
    type:                string
    code:                string
    label:               string
    amount:              number
    isImposable:         boolean
    isSubjectToIpres:    boolean
    isSubjectToCss:      boolean
    isSubjectToCfce:     boolean
    isDeduction:         boolean
  }>
}

export interface PayrollCalculationLine {
  type:        string
  code:        string
  label:       string
  base?:       number
  rate?:       number
  quantity?:   number
  amount:      number
  isDeduction: boolean
  order:       number
}

export interface PayrollCalculationResult {
  grossSalary:       number
  taxableMonthly:    number
  totalDeductions:   number
  netSalary:         number
  netPayable:        number
  employerCharges:   number
  costForEmployer:   number
  lines:             PayrollCalculationLine[]
  parametersSnapshot: any
}

export interface GrossUpDto {
  netTarget:         number
  fiscalParts:       number
  isCadre:           boolean
  daysPerMonth?:     number
  daysWorked?:       number
  fixedComponents?:  PreviewPayrollDto['components']
}

export interface GrossUpResult {
  baseSalary:    number
  grossSalary:   number
  netPayable:    number
  iterations:    number
  details:       PayrollCalculationResult
}

// ============================================================================
//  API — Employés
// ============================================================================

export const hrApi = {
  // Employés
  employees: {
    list: (params?: Record<string, any>) =>
      api
        .get<{
          employees: Employee[]
          total: number
          stats: EmployeeStats
        }>('/hr/employees', { params })
        .then((r) => r.data?.data ?? r.data),
    get: (id: string) =>
      api.get<Employee>(`/hr/employees/${id}`).then((r) => r.data?.data ?? r.data),
    create: (dto: any) =>
      api.post<Employee>('/hr/employees', dto).then((r) => r.data?.data ?? r.data),
    update: (id: string, dto: any) =>
      api.patch<Employee>(`/hr/employees/${id}`, dto).then((r) => r.data?.data ?? r.data),

    delete: (id: string, deleteUser = false) =>
      api.delete(`/hr/employees/${id}?deleteUser=${deleteUser}`).then((r) => r.data?.data ?? r.data),
    terminate: (
      id: string,
      dto: { terminationDate: string; terminationReason: string; terminationType: string },
    ) =>
      api.post<Employee>(`/hr/employees/${id}/terminate`, dto).then((r) => r.data?.data ?? r.data),
    departments: (entityId?: string) =>
      api
        .get<{ id: string; name: string; employeeCount: number }[]>('/hr/departments')
        .then((r) => r.data?.data ?? r.data),
  },

  // Contrats
  contracts: {
    listByEmployee: (employeeId: string) =>
      api
        .get<{ contracts: Contract[] }>(`/hr/employees/${employeeId}/contracts`)
        .then((r) => r.data?.data ?? r.data),
    get: (id: string) =>
      api
        .get<Contract>(`/hr/contracts/${id}`)
        .then((r) => r.data?.data ?? r.data),
    create: (dto: any) =>
      api.post<Contract>('/hr/contracts', dto).then((r) => r.data?.data ?? r.data),
    renew: (id: string, dto: any) =>
      api.post<Contract>(`/hr/contracts/${id}/renew`, dto).then((r) => r.data?.data ?? r.data),
    terminate: (id: string, dto: any) =>
      api.post<Contract>(`/hr/contracts/${id}/terminate`, dto).then((r) => r.data?.data ?? r.data),
  },

  // Paie — Périodes
  payroll: {
    listPeriods: (params?: { year?: number; page?: number }) =>
      api
        .get<{ periods: PayrollPeriod[]; total: number }>('/hr/payroll/periods', { params })
        .then((r) => r.data?.data ?? r.data),
    getPeriod: (id: string) =>
      api
        .get<PayrollPeriod & { payslips: Payslip[]; stats: any }>(`/hr/payroll/periods/${id}`)
        .then((r) => r.data?.data ?? r.data),
    openPeriod: (dto: { year: number; month: number }) =>
      api.post<PayrollPeriod>('/hr/payroll/periods', dto).then((r) => r.data?.data ?? r.data),
    initializePayslips: (id: string) =>
      api.post<any>(`/hr/payroll/periods/${id}/initialize`).then((r) => r.data?.data ?? r.data),
    calculate: (id: string) =>
      api.post<any>(`/hr/payroll/periods/${id}/calculate`).then((r) => r.data?.data ?? r.data),
    getStatus: (id: string) =>
      api.get<any>(`/hr/payroll/periods/${id}/status`).then((r) => r.data?.data ?? r.data),
    validate: (id: string) =>
      api
        .post<PayrollPeriod>(`/hr/payroll/periods/${id}/validate`)
        .then((r) => r.data?.data ?? r.data),
    approve: (id: string, dto?: { comment?: string }) =>
      api
        .post<PayrollPeriod>(`/hr/payroll/periods/${id}/approve`, dto)
        .then((r) => r.data?.data ?? r.data),
    markPaid: (id: string, dto: { paymentDate: string; paymentRef: string; paymentMethod: 'BANK_TRANSFER' | 'CHECK' | 'CASH' }) =>
      api
        .post<PayrollPeriod>(`/hr/payroll/periods/${id}/mark-paid`, dto)
        .then((r) => r.data?.data ?? r.data),
    lock: (id: string, dto: { reason: string }) =>
      api.post<any>(`/hr/payroll/periods/${id}/lock`, dto).then((r) => r.data?.data ?? r.data),
    getMasseSalariale: (id: string) =>
      api.get<any>(`/hr/payroll/periods/${id}/masse-salariale`).then((r) => r.data?.data ?? r.data),
    getDeclarationIpres: (id: string) =>
      api
        .get<any>(`/hr/payroll/periods/${id}/declaration-ipres`)
        .then((r) => r.data?.data ?? r.data),
    getDeclarationDipe: (id: string) =>
      api
        .get<any>(`/hr/payroll/periods/${id}/declaration-dipe`)
        .then((r) => r.data?.data ?? r.data),
    getPayslip: (id: string) =>
      api.get<Payslip>(`/hr/payroll/payslips/${id}`).then((r) => r.data?.data ?? r.data),
    getEmployeePayslips: (employeeId: string, params?: { year?: number }) =>
      api
        .get<{
          payslips: Payslip[]
          total: number
        }>(`/hr/payroll/employees/${employeeId}/payslips`, { params })
        .then((r) => r.data?.data ?? r.data),

    // ─── Calcul (moteur de paie) ─────────────────────────────────────────
    calculatePayslip: (payslipId: string) =>
      api
        .post<PayrollCalculationResult>(`/hr/payroll/payslips/${payslipId}/calculate`)
        .then((r) => r.data?.data ?? r.data),

    preview: (dto: PreviewPayrollDto) =>
      api
        .post<PayrollCalculationResult>('/hr/payroll/preview', dto)
        .then((r) => r.data?.data ?? r.data),

    grossUp: (dto: GrossUpDto) =>
      api
        .post<GrossUpResult>('/hr/payroll/gross-up', dto)
        .then((r) => r.data?.data ?? r.data),

    // ─── Variables paie ─────────────────────────────────────────────────
    listVariables: (payslipId: string) =>
      api
        .get<PayrollVariable[]>(`/hr/payroll/payslips/${payslipId}/variables`)
        .then((r) => r.data?.data ?? r.data),

    createVariable: (payslipId: string, dto: CreatePayrollVariableDto) =>
      api
        .post<PayrollVariable>(`/hr/payroll/payslips/${payslipId}/variables`, dto)
        .then((r) => r.data?.data ?? r.data),

    updateVariable: (variableId: string, dto: Partial<CreatePayrollVariableDto>) =>
      api
        .patch<PayrollVariable>(`/hr/payroll/variables/${variableId}`, dto)
        .then((r) => r.data?.data ?? r.data),

    deleteVariable: (variableId: string) =>
      api
        .delete<{ deleted: boolean }>(`/hr/payroll/variables/${variableId}`)
        .then((r) => r.data?.data ?? r.data),

    // ─── PDF ────────────────────────────────────────────────────────────
    generatePdf: (payslipId: string) =>
      api
        .post<{
          payslipPdfId: string
          pdfUrl:        string
          pdfHash:       string
          pdfSize:       number
          presignedUrl:  string
        }>(`/hr/payroll/payslips/${payslipId}/pdf`)
        .then((r) => r.data?.data ?? r.data),

    getPdfDownloadUrl: (payslipId: string) =>
      api
        .get<{
          url:       string
          expiresIn: number
          sealed:    boolean
          sealedAt:  string | null
        }>(`/hr/payroll/payslips/${payslipId}/pdf/download`)
        .then((r) => r.data?.data ?? r.data),

    verifyPdfIntegrity: (payslipId: string) =>
      api
        .get<{
          payslipId:    string
          expectedHash: string
          computedHash: string
          integrityOk:  boolean
          pdfSize:      number
        }>(`/hr/payroll/payslips/${payslipId}/pdf/verify`)
        .then((r) => r.data?.data ?? r.data),

    sealPdf: (payslipId: string) =>
      api
        .post<{
          payslipId:       string
          wormStorageKey:  string
          wormChecksum:    string
          sealedAt:        string
          alreadySealed:   boolean
        }>(`/hr/payroll/payslips/${payslipId}/seal`)
        .then((r) => r.data?.data ?? r.data),

    // ─── Batch operations ───────────────────────────────────────────────
    generatePdfBatch: (periodId: string) =>
      api
        .post<{ jobId: string; status: string }>(`/hr/payroll/periods/${periodId}/pdf-batch`)
        .then((r) => r.data?.data ?? r.data),

    sealPeriod: (periodId: string) =>
      api
        .post<{
          total:    number
          sealed:   number
          skipped:  number
          errors:   number
          details:  Array<{ payslipId: string; status: string; error?: string }>
        }>(`/hr/payroll/periods/${periodId}/seal`)
        .then((r) => r.data?.data ?? r.data),

    // ─── Composants salaire (Contrat) ───────────────────────────────────
    listComponents: (contractId: string) =>
      api
        .get<ContractSalaryComponent[]>(`/hr/payroll/contracts/${contractId}/components`)
        .then((r) => r.data?.data ?? r.data),

    createComponent: (contractId: string, dto: CreateContractComponentDto) =>
      api
        .post<ContractSalaryComponent>(`/hr/payroll/contracts/${contractId}/components`, dto)
        .then((r) => r.data?.data ?? r.data),

    createComponentsBulk: (contractId: string, items: CreateContractComponentDto[]) =>
      api
        .post<{ count: number }>(`/hr/payroll/contracts/${contractId}/components/bulk`, { items })
        .then((r) => r.data?.data ?? r.data),

    updateComponent: (componentId: string, dto: Partial<CreateContractComponentDto>) =>
      api
        .patch<ContractSalaryComponent>(`/hr/payroll/components/${componentId}`, dto)
        .then((r) => r.data?.data ?? r.data),

    deleteComponent: (componentId: string) =>
      api
        .delete<{ deleted: boolean }>(`/hr/payroll/components/${componentId}`)
        .then((r) => r.data?.data ?? r.data),
  },

  // Congés
  leaves: {
    getTypes: () => api.get<LeaveType[]>('/hr/leaves/types').then((r) => r.data?.data ?? r.data),
    getBalances: (employeeId: string, year?: number) =>
      api
        .get<LeaveBalance[]>(`/hr/leaves/employees/${employeeId}/balances`, { params: { year } })
        .then((r) => r.data?.data ?? r.data),
    list: (params?: Record<string, any>) =>
      api
        .get<{ requests: LeaveRequest[]; total: number }>('/hr/leaves', { params })
        .then((r) => r.data?.data ?? r.data),
    get: (id: string) =>
      api.get<LeaveRequest>(`/hr/leaves/${id}`).then((r) => r.data?.data ?? r.data),
    create: (dto: any) =>
      api.post<LeaveRequest>('/hr/leaves', dto).then((r) => r.data?.data ?? r.data),
    approveByManager: (id: string, comment?: string) =>
      api
        .post<LeaveRequest>(`/hr/leaves/${id}/approve-manager`, { comment })
        .then((r) => r.data?.data ?? r.data),
    approveByHr: (id: string, comment?: string) =>
      api
        .post<LeaveRequest>(`/hr/leaves/${id}/approve-hr`, { comment })
        .then((r) => r.data?.data ?? r.data),
    reject: (id: string, reason: string) =>
      api
        .post<LeaveRequest>(`/hr/leaves/${id}/reject`, { reason })
        .then((r) => r.data?.data ?? r.data),
    cancel: (id: string, reason?: string) =>
      api
        .post<LeaveRequest>(`/hr/leaves/${id}/cancel`, { reason })
        .then((r) => r.data?.data ?? r.data),
    getCalendar: (params: { startDate: string; endDate: string; departmentId?: string }) =>
      api
        .get<LeaveRequest[]>('/hr/leaves/calendar', { params })
        .then((r) => r.data?.data ?? r.data),
    getStats: (year?: number) =>
      api.get<any>('/hr/leaves/stats', { params: { year } }).then((r) => r.data?.data ?? r.data),
    initBalances: (year: number) =>
      api.post<any>('/hr/leaves/balances/initialize', { year }).then((r) => r.data?.data ?? r.data),
  },

  // Pointage
  attendance: {
    clockIn: (dto?: { notes?: string }) =>
      api
        .post<Attendance>('/hr/attendance/clock-in', dto ?? {})
        .then((r) => r.data?.data ?? r.data),
    clockOut: (dto?: { breakMinutes?: number; notes?: string }) =>
      api
        .post<Attendance>('/hr/attendance/clock-out', dto ?? {})
        .then((r) => r.data?.data ?? r.data),
    getDailyOverview: (date?: string) =>
      api
        .get<DailyOverview>('/hr/attendance/overview', { params: { date } })
        .then((r) => r.data?.data ?? r.data),
    getEmployeeAttendance: (
      employeeId: string,
      params?: { startDate?: string; endDate?: string },
    ) =>
      api
        .get<{
          attendances: Attendance[]
          total: number
        }>(`/hr/attendance/employees/${employeeId}`, { params })
        .then((r) => r.data?.data ?? r.data),
    getMonthlyReport: (employeeId: string, year: number, month: number) =>
      api
        .get<any>(`/hr/attendance/employees/${employeeId}/report`, { params: { year, month } })
        .then((r) => r.data?.data ?? r.data),
    getOvertimeReport: (params: { startDate: string; endDate: string }) =>
      api.get<any>('/hr/attendance/overtime', { params }).then((r) => r.data?.data ?? r.data),
    edit: (id: string, dto: any) =>
      api.patch<Attendance>(`/hr/attendance/${id}`, dto).then((r) => r.data?.data ?? r.data),
    getRules: () => api.get<AttendanceRule[]>('/hr/attendance/rules').then((r) => r.data?.data ?? r.data),
    createRule: (dto: Partial<AttendanceRule>) =>
      api.post<AttendanceRule>('/hr/attendance/rules', dto).then((r) => r.data?.data ?? r.data),
    updateRule: (id: string, dto: Partial<AttendanceRule>) =>
      api.patch<AttendanceRule>(`/hr/attendance/rules/${id}`, dto).then((r) => r.data?.data ?? r.data),
    deleteRule: (id: string) =>
      api.delete<{ message: string }>(`/hr/attendance/rules/${id}`).then((r) => r.data?.data ?? r.data),
  },

  // Carrière
  career: {
    getCampaigns: (params?: any) =>
      api
        .get<{ campaigns: EvaluationCampaign[]; total: number }>('/hr/career/campaigns', { params })
        .then((r) => r.data?.data ?? r.data),
    getCampaign: (id: string) =>
      api.get<EvaluationCampaign>(`/hr/career/campaigns/${id}`).then((r) => r.data?.data ?? r.data),
    createCampaign: (dto: any) =>
      api.post<EvaluationCampaign>('/hr/career/campaigns', dto).then((r) => r.data?.data ?? r.data),
    activateCampaign: (id: string) =>
      api
        .post<EvaluationCampaign>(`/hr/career/campaigns/${id}/activate`)
        .then((r) => r.data?.data ?? r.data),
    initializeEvaluations: (id: string) =>
      api.post<any>(`/hr/career/campaigns/${id}/initialize`).then((r) => r.data?.data ?? r.data),
    getEvaluation: (id: string) =>
      api
        .get<PerformanceEvaluation>(`/hr/career/evaluations/${id}`)
        .then((r) => r.data?.data ?? r.data),
    getEmployeeEvaluations: (employeeId: string) =>
      api
        .get<PerformanceEvaluation[]>(`/hr/career/employees/${employeeId}/evaluations`)
        .then((r) => r.data?.data ?? r.data),
    submitSelfEval: (id: string, dto: any) =>
      api
        .post<PerformanceEvaluation>(`/hr/career/evaluations/${id}/self-eval`, dto)
        .then((r) => r.data?.data ?? r.data),
    submitManagerEval: (id: string, dto: any) =>
      api
        .post<PerformanceEvaluation>(`/hr/career/evaluations/${id}/manager-eval`, dto)
        .then((r) => r.data?.data ?? r.data),
    getTrainingCatalog: () =>
      api.get<TrainingCatalog[]>('/hr/career/trainings').then((r) => r.data?.data ?? r.data),
    getEmployeeTrainings: (employeeId: string) =>
      api
        .get<any[]>(`/hr/career/employees/${employeeId}/trainings`)
        .then((r) => r.data?.data ?? r.data),
    createTraining: (dto: any) =>
      api.post<any>('/hr/career/trainings', dto).then((r) => r.data?.data ?? r.data),
    assignTraining: (dto: any) =>
      api.post<any>('/hr/career/trainings/assign', dto).then((r) => r.data?.data ?? r.data),
    getCareerHistory: (employeeId: string) =>
      api
        .get<CareerHistory[]>(`/hr/career/employees/${employeeId}/history`)
        .then((r) => r.data?.data ?? r.data),
    addCareerEvent: (dto: any) =>
      api.post<CareerHistory>('/hr/career/events', dto).then((r) => r.data?.data ?? r.data),
    getStats: (year?: number) =>
      api.get<any>('/hr/career/stats', { params: { year } }).then((r) => r.data?.data ?? r.data),
  },

  // Sprint 2 — PDF absence + impact projet
  generateAbsencePdf: (leaveId: string) =>
    api.post<{ url: string }>(`/hr/leaves/${leaveId}/pdf`).then((r) => r.data?.data ?? r.data),
  getAbsenceProjectImpact: (leaveId: string) =>
    api.get<any>(`/hr/leaves/${leaveId}/project-impact`).then((r) => r.data?.data ?? r.data),

  // Sprint 3 — Export mensuel pointage
  getMonthlyAttendanceExport: (year: number, month: number) =>
    api.get<any>('/hr/attendance/monthly-export', { params: { year, month } }).then((r) => r.data?.data ?? r.data),
}
